import React from 'react';
import CloudUploadPanel, { UPLOAD_MAX_BYTES, UPLOAD_TYPES } from '../components/CloudUploadPanel';

const getToken = () => new URLSearchParams(window.location.search).get('token') || '';

const PartnerUploadWidget = () => {
    const [context, setContext] = React.useState(null);
    const [contextError, setContextError] = React.useState('');
    const [loading, setLoading] = React.useState(true);

    const [file, setFile] = React.useState(null);
    const [preview, setPreview] = React.useState(null);
    const [dimensions, setDimensions] = React.useState(null);
    const [status, setStatus] = React.useState('idle');
    const [progress, setProgress] = React.useState(0);
    const [error, setError] = React.useState('');
    const [result, setResult] = React.useState(null);
    const blobUrlRef = React.useRef(null);
    const token = React.useRef(getToken()).current;

    const parents = React.useMemo(() => context?.allowedOrigins || [], [context]);

    const tell = React.useCallback((payload) => {
        const data = { source: 'franklycloud', ...payload };
        parents.forEach((origin) => {
            try { window.parent.postMessage(data, origin); } catch { /* ignore */ }
        });
    }, [parents]);

    const revokeBlobUrl = React.useCallback(() => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    }, []);

    React.useEffect(() => () => revokeBlobUrl(), [revokeBlobUrl]);

    React.useEffect(() => {
        if (!token) {
            setContextError('This upload session is not valid.');
            setLoading(false);
            return;
        }
        fetch(`/franklycloud/context?token=${encodeURIComponent(token)}`)
            .then(r => r.json())
            .then((r) => {
                if (r?.success) setContext(r);
                else setContextError(r?.message || 'This upload session is not valid.');
            })
            .catch(() => setContextError('Upload is unavailable right now.'))
            .finally(() => setLoading(false));
    }, [token]);

    React.useEffect(() => {
        if (context) tell({ type: 'ready' });
    }, [context, tell]);

    const handleFileChosen = (chosen) => {
        setError('');

        if (!UPLOAD_TYPES.includes(chosen.type)) {
            setError('Only JPEG, PNG, GIF, and WebP images are accepted.');
            setStatus('rejected');
            tell({ type: 'error', error: 'bad_type' });
            return;
        }

        if (chosen.size > UPLOAD_MAX_BYTES) {
            setError(`Image is too large (max ${Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))}MB). Please use a smaller file.`);
            setStatus('rejected');
            tell({ type: 'error', error: 'file_too_large' });
            return;
        }

        setStatus('validating');
        revokeBlobUrl();
        const url = URL.createObjectURL(chosen);
        blobUrlRef.current = url;
        setPreview(url);

        const img = new Image();
        img.onload = () => {
            setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
            setFile(chosen);
            setStatus('ready');
        };
        img.onerror = () => {
            setError('Could not read image. Please try another file.');
            setStatus('rejected');
            revokeBlobUrl();
        };
        img.src = url;
    };

    const handleReset = () => {
        revokeBlobUrl();
        setFile(null);
        setPreview(null);
        setDimensions(null);
        setStatus('idle');
        setProgress(0);
        setError('');
        setResult(null);
    };

    const handleSubmit = () => {
        if (!file || status !== 'ready') return;

        setStatus('uploading');
        setProgress(2);

        const fd = new FormData();
        fd.append('token', token);
        fd.append('image', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/franklycloud/upload');

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.max(2, Math.round((e.loaded / e.total) * 70)));
        };

        xhr.onload = () => {
            let data = null;
            try { data = JSON.parse(xhr.responseText); } catch { data = null; }

            if (data?.success) {
                setProgress(100);
                setStatus('done');
                setResult(data);
                tell({ type: 'uploaded', url: data.url, width: data.width, height: data.height, bytes: data.bytes });
            } else {
                setError(data?.reason || data?.message || 'Your image could not be uploaded.');
                setStatus('rejected');
                setProgress(0);
                tell({ type: 'error', error: data?.error || 'upload_failed', message: data?.message || '' });
            }
        };

        xhr.onerror = () => {
            setError('Upload failed. Please check your connection and try again.');
            setStatus('error');
            setProgress(0);
            tell({ type: 'error', error: 'network' });
        };

        xhr.send(fd);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-offwhite" />
        );
    }

    if (contextError) {
        return (
            <div className="min-h-screen bg-offwhite flex items-center justify-center p-4">
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{contextError}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-offwhite flex flex-col">
            <div className="flex items-center gap-2 px-3 py-2 bg-coal text-offwhite shrink-0">
                <img src="/img/franklycloud.png" alt="" className="shrink-0 h-4 w-auto" />
                <span className="font-['Jersey_10'] text-lg uppercase tracking-wide leading-none">Upload to FranklyCloud</span>
            </div>
            <CloudUploadPanel
                status={status}
                preview={preview}
                file={file}
                dimensions={dimensions}
                progress={progress}
                error={error}
                doneLabel="Image uploaded!"
                onFileChosen={handleFileChosen}
                onReset={handleReset}
                onSubmit={handleSubmit}
                compact
            />
            {result && (
                <div className="px-3 pb-3 flex flex-col gap-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-coal/50">Image URL</p>
                    <input
                        readOnly
                        value={result.url}
                        onFocus={(e) => e.target.select()}
                        className="w-full border border-black/20 px-2 py-1 text-[11px] font-mono bg-white"
                    />
                    <a
                        href={result.path || result.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] text-blue-600 underline"
                    >
                        Open the stored image
                    </a>
                </div>
            )}

        </div>
    );
};

export default PartnerUploadWidget;
